/**
 * Leaderboard Tab Component
 *
 * Matches original HTML structure: .leaderboard-container, .leaderboard-table with song rankings.
 * Shows SONGS ranked by score, not users.
 */

import { useState, useEffect } from 'preact/hooks';
import { selectedGroup } from '@/stores/groups';
import { isAuthenticated } from '@/stores/auth';
import { api } from '@/api/client';
import { play, currentTrackId, isPlaying } from '@/stores/player';
import type { Song } from '@/types';

interface LeaderboardSong extends Song {
  group?: {
    id: string;
    name: string;
  };
}

export function LeaderboardTab() {
  const [songs, setSongs] = useState<LeaderboardSong[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('current');
  const [sortBy, setSortBy] = useState('likes');
  const [stats, setStats] = useState({ totalSongs: 0, totalLikes: 0, totalVotes: 0 });

  useEffect(() => {
    if (isAuthenticated.value) {
      fetchLeaderboard();
    }
  }, [timeframe, sortBy]);

  const fetchLeaderboard = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{ songs: LeaderboardSong[]; stats: typeof stats }>(
        `/api/songs/leaderboard/global?timeframe=${timeframe}&sort=${sortBy}`
      );
      if (response.data?.songs) {
        setSongs(response.data.songs);
      }
      if (response.data?.stats) {
        setStats(response.data.stats);
      }
    } catch (e) {
      console.error('Failed to fetch leaderboard:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlaySong = (song: LeaderboardSong) => {
    play(song);
  };

  if (!isAuthenticated.value) {
    return (
      <div class="leaderboard-container">
        <div class="welcome-section">
          <h2>Sign in to view leaderboard</h2>
          <p>See how songs rank across all pods</p>
        </div>
      </div>
    );
  }

  return (
    <div class="leaderboard-container">
      {/* Leaderboard title */}
      <h2 class="section-title">
        <span class="global-rankings-title">
          <span class="global-icon">🌎</span>
          Global Leaderboard
        </span>
      </h2>

      {/* Stats display */}
      <div class="leaderboard-stats">
        <div class="leaderboard-stat">
          <div class="stat-number">{stats.totalSongs}</div>
          <div class="stat-label">Songs</div>
        </div>
        <div class="leaderboard-stat">
          <div class="stat-number">{stats.totalLikes}</div>
          <div class="stat-label">Total Upvotes</div>
        </div>
        <div class="leaderboard-stat">
          <div class="stat-number">{stats.totalVotes}</div>
          <div class="stat-label">Total Votes</div>
        </div>
      </div>

      {/* Leaderboard filters */}
      <div class="leaderboard-filters">
        <div class="filter-item">
          <label for="leaderboard-timeframe">Timeframe</label>
          <select
            id="leaderboard-timeframe"
            value={timeframe}
            onChange={(e) => setTimeframe((e.target as HTMLSelectElement).value)}
          >
            <option value="current">Current Period</option>
            <option value="all-time">All Time</option>
            <option value="2025">2025</option>
            <option value="2024">2024</option>
          </select>
        </div>

        <div class="filter-item">
          <label for="leaderboard-sort">Sort By</label>
          <select
            id="leaderboard-sort"
            value={sortBy}
            onChange={(e) => setSortBy((e.target as HTMLSelectElement).value)}
          >
            <option value="likes">Highest Score</option>
            <option value="votes">Most Votes</option>
            <option value="date">Newest</option>
          </select>
        </div>
      </div>

      {/* Leaderboard table */}
      <div class="leaderboard-table-container">
        {isLoading ? (
          <div class="leaderboard-loading">
            <div class="loading-spinner"></div>
            <p>Loading global leaderboard data...</p>
          </div>
        ) : songs.length === 0 ? (
          <div class="leaderboard-empty">
            <div class="empty-state-container">
              <div class="empty-state-content">
                <div class="empty-state-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <p class="empty-state-title">No Songs Found</p>
                <p class="empty-state-subtitle">
                  No songs have been submitted yet for this period
                </p>
              </div>
            </div>
          </div>
        ) : (
          <table class="leaderboard-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th></th>{/* Cover art column */}
                <th>Song</th>
                <th>Artist</th>
                <th>Album</th>
                <th>Submitter</th>
                <th>Pod</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {songs.map((song, index) => {
                const rank = index + 1;
                const rankClass = rank <= 3 ? `rank-${rank}` : '';
                const isCurrentlyPlaying = currentTrackId.value === song.spotifyId && isPlaying.value;

                return (
                  <tr
                    key={song.id}
                    class={rankClass}
                    data-song-index={index}
                    data-spotify-track-group="global-songs"
                    data-spotify-id={song.spotifyId}
                    tabIndex={0}
                  >
                    <td class="rank-cell">
                      <span class="rank-number">{rank}</span>
                    </td>
                    <td class="cover-cell">
                      <div class="song-thumbnail-container">
                        <img
                          src={song.albumArt || '/api/placeholder/40/40'}
                          alt={song.title}
                          class="song-thumbnail"
                        />
                      </div>
                    </td>
                    <td class="song-cell">
                      <div
                        class={`song-title-play-btn ${isCurrentlyPlaying ? 'playing' : ''}`}
                        data-spotify-id={song.spotifyId}
                        onClick={() => handlePlaySong(song)}
                      >
                        <div class="play-icon"></div>
                        <div class="pause-icon"></div>
                      </div>
                      <a
                        href={`https://open.spotify.com/track/${song.spotifyId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="spotify-content-link"
                      >
                        <span class="song-title">{song.title}</span>
                      </a>
                    </td>
                    <td class="artist-cell">
                      <a
                        href={`https://open.spotify.com/artist/${song.artistId || ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="spotify-content-link"
                      >
                        {song.artist}
                      </a>
                    </td>
                    <td class="album-cell">
                      <a
                        href={`https://open.spotify.com/album/${song.albumId || ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="spotify-content-link album-name"
                      >
                        {song.album || ''}
                      </a>
                    </td>
                    <td class="submitter-cell">
                      {song.userId && song.userName !== 'Anonymous' ? (
                        <a href={`/user/${song.userId}`} class="user-name-link">
                          {song.userName}
                        </a>
                      ) : (
                        song.userName || 'Anonymous'
                      )}
                    </td>
                    <td class="pod-cell">
                      <div class="pod-indicator">
                        {song.group?.name || selectedGroup.value?.name || 'Unknown Pod'}
                      </div>
                    </td>
                    <td class="likes-cell">
                      <span class="likes-count">+{song.votes?.like || song.votes?.score || 0}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
